import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import {
  acceptJoinTravelRequest,
  cancelTravel,
  claimTravelSpawn,
  fetchActiveTravel,
  fetchActiveTravelByAdventure,
  joinTravelProposal,
  leaveTravelParty,
  leaveTravelProposal,
  pauseTravel,
  rejectJoinTravelRequest,
  requestJoinTravel,
  resumeTravel,
  startTravel,
  startTravelProposal,
} from './api'
import AppContext from '../../contexts/AppContext'
import { ADVENTURE_SETUP_ID, replyAnchorId } from '../adventure/shared/adventureScroll'
import TravelItineraryPicker, {
  ROUTE_TYPE_LABEL as ITIN_ROUTE_LABEL,
} from './TravelItineraryPicker'
import { formatHanLabel } from '../../components/TerritoryGrid/territoryUtils'
import './TravelPanel.css'

const POLL_MS = 20000
const OPENING_MIN = 10
const ROUTE_TYPE_LABEL = { ...ITIN_ROUTE_LABEL, safe: 'Segura', fast: 'Rápida', dangerous: 'Peligrosa' }
const STATUS_LABEL = {
  traveling: 'En camino',
  event_paused: 'Pausado',
  completed: 'Completado',
  cancelled: 'Cancelado',
  abandoned: 'Abandonado',
}

function statusLabel(status) {
  return STATUS_LABEL[status] || status || '—'
}

function statusModifier(status) {
  if (status === 'event_paused') return 'paused'
  if (status === 'traveling') return 'traveling'
  return 'idle'
}

function formatRemainingMs(diffMs) {
  const sec = Math.round(Math.max(0, diffMs) / 1000)
  if (sec <= 0) return '0s'
  if (sec < 60) return `${sec}s`
  const min = Math.floor(sec / 60)
  const rem = sec % 60
  if (min < 60) return `${min}m ${rem}s`
  const h = Math.floor(min / 60)
  return `${h}h ${min % 60}m`
}

/**
 * ETA viva en traveling; congelada en event_paused (remainingMsAtPause o eta - pausedAt).
 */
function formatEta(session, nowMs = Date.now()) {
  if (!session?.eta) return '—'
  const etaMs = new Date(session.eta).getTime()
  if (Number.isNaN(etaMs)) return '—'

  if (session.status === 'event_paused') {
    const stored = session.progress?.remainingMsAtPause
    let remainingMs =
      stored != null && Number.isFinite(Number(stored)) ? Number(stored) : null
    if (remainingMs == null && session.pausedAt) {
      const pausedMs = new Date(session.pausedAt).getTime()
      remainingMs = Number.isNaN(pausedMs) ? 0 : Math.max(0, etaMs - pausedMs)
    }
    if (remainingMs == null) remainingMs = 0
    const label = formatRemainingMs(remainingMs)
    return remainingMs <= 0 ? 'Pausado' : `${label} (pausado)`
  }

  return formatRemainingMs(etaMs - nowMs)
}

/**
 * Progreso del hop actual [0..1] según startedAt / eta / pausas.
 */
function hopProgressRatio(session, nowMs = Date.now()) {
  if (!session?.startedAt || !session?.eta) return 0
  const startedMs = new Date(session.startedAt).getTime()
  const etaMs = new Date(session.eta).getTime()
  if (Number.isNaN(startedMs) || Number.isNaN(etaMs)) return 0

  const pausedAccum = Number(session.progress?.elapsedPausedMs) || 0
  let remainingMs
  let clockMs = nowMs

  if (session.status === 'event_paused') {
    const stored = session.progress?.remainingMsAtPause
    if (stored != null && Number.isFinite(Number(stored))) {
      remainingMs = Math.max(0, Number(stored))
    } else if (session.pausedAt) {
      const pausedAtMs = new Date(session.pausedAt).getTime()
      remainingMs = Number.isNaN(pausedAtMs) ? 0 : Math.max(0, etaMs - pausedAtMs)
    } else {
      remainingMs = Math.max(0, etaMs - nowMs)
    }
    const pausedAtMs = session.pausedAt ? new Date(session.pausedAt).getTime() : NaN
    clockMs = Number.isNaN(pausedAtMs) ? nowMs : pausedAtMs
  } else {
    remainingMs = Math.max(0, etaMs - nowMs)
  }

  const elapsedMs = Math.max(0, clockMs - startedMs - pausedAccum)
  const totalMs = elapsedMs + remainingMs
  if (totalMs <= 0) return session.status === 'traveling' ? 0 : 1
  return Math.min(1, Math.max(0, elapsedMs / totalMs))
}

function openingHref(opening, adventureSlug) {
  if (opening?.hrefHint) return opening.hrefHint
  const slug = adventureSlug
  if (!slug) return null
  if (opening?.kind === 'setup') return `/aventura/${slug}#${ADVENTURE_SETUP_ID}`
  if (opening?.kind === 'reply' && opening.replyId != null) {
    return `/aventura/${slug}#${replyAnchorId(opening.replyId)}`
  }
  return null
}

/**
 * Panel de viaje (Subfase E/G + apertura #38).
 * @param {'full'|'monitor'} mode — monitor: solo estado + enlace al tema (ficha)
 * @param {number|null} parentAdventureId — nested / tema actual; null = autónomo (crea tema)
 * @param {boolean} onlyForThisAdventure — si true, ignora sesión de otra aventura (idle local)
 * @param {boolean} allowStart — si false, no muestra formulario de inicio; sin sesión → null
 * @param {number|null} observeAdventureId — carga viaje por crónica (observadores)
 * @param {boolean} isAdventureMaster — Master activo de la crónica (cancelar / pausar / hard resume)
 */
export default function TravelPanel({
  characterId,
  homeSlug: homeSlugProp = null,
  parentAdventureId = null,
  mode = 'full',
  onlyForThisAdventure = false,
  allowStart = true,
  observeAdventureId = null,
  isAdventureMaster = false,
  className = '',
}) {
  const navigate = useNavigate()
  const { hasPermission } = useContext(AppContext)
  const canTravelPausePerm = !!(hasPermission && hasPermission('travel.pause'))
  const canTravelGmPerm = !!(hasPermission && hasPermission('travel.gm'))
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [session, setSession] = useState(null)
  const [adventureMeta, setAdventureMeta] = useState(null)
  const [opening, setOpening] = useState(null)
  const [pendingHard, setPendingHard] = useState(null)
  const [spawns, setSpawns] = useState([])
  const [originSlug, setOriginSlug] = useState(homeSlugProp)
  const [destSlug, setDestSlug] = useState('')
  const [via, setVia] = useState([])
  const [routeType, setRouteType] = useState('safe')
  const [openingNarrative, setOpeningNarrative] = useState('')
  const [provisionSummary, setProvisionSummary] = useState(null)
  const [itinerary, setItinerary] = useState(null)
  const [proposal, setProposal] = useState(null)
  const [roster, setRoster] = useState([])
  const [pendingJoinRequests, setPendingJoinRequests] = useState([])
  const [nowTick, setNowTick] = useState(() => Date.now())

  const prevSessionRef = useRef(undefined)
  const prevHomeRef = useRef(homeSlugProp)

  const openingTrimmed = openingNarrative.trim()
  const openingOk = openingTrimmed.length >= OPENING_MIN

  const refresh = useCallback(async () => {
    if (!observeAdventureId && !characterId) return
    try {
      const active = observeAdventureId
        ? await fetchActiveTravelByAdventure(observeAdventureId)
        : await fetchActiveTravel(characterId)
      let nextSession = active.session || null
      let nextAdventure = active.adventure || null
      let nextOpening = active.opening || null
      let nextPendingHard = active.pendingHard || null

      if (
        !observeAdventureId &&
        onlyForThisAdventure &&
        parentAdventureId &&
        nextSession &&
        Number(nextSession.parentAdventureId) !== Number(parentAdventureId)
      ) {
        nextSession = null
        nextAdventure = null
        nextOpening = null
        nextPendingHard = null
      }

      const prev = prevSessionRef.current
      const viewerOwnsPrev =
        characterId != null &&
        prev &&
        Number(prev.characterId) === Number(characterId)
      if (prev && !nextSession && viewerOwnsPrev) {
        const dest = prev.destSlug
        toast.success(
          dest ? (
            <span>
              Has llegado a{' '}
              <Link to={`/han/${dest}`} style={{ textDecoration: 'underline' }}>
                {formatHanLabel(dest)}
              </Link>
            </span>
          ) : (
            'Viaje completado'
          )
        )
      }
      prevSessionRef.current = nextSession
      setSession(nextSession)
      setAdventureMeta(nextAdventure)
      setOpening(nextOpening)
      setPendingHard(nextPendingHard)
      setSpawns(active.spawns || [])
      setProvisionSummary(active.provisionSummary || null)
      setItinerary(
        active.itinerary ||
          nextSession?.progress?.itinerary ||
          (active.proposal?.waypoints
            ? {
                waypoints: active.proposal.waypoints,
                hopIndex: 0,
                preferredRouteType: active.proposal.preferredRouteType || 'safe',
                finalDestSlug:
                  active.proposal.waypoints[active.proposal.waypoints.length - 1] || null,
              }
            : null)
      )
      setProposal(active.proposal || null)
      setRoster(Array.isArray(active.roster) ? active.roster : [])
      setPendingJoinRequests(
        Array.isArray(active.pendingJoinRequests) ? active.pendingJoinRequests : []
      )

      if (!nextSession && !active.proposal && mode !== 'monitor') {
        setOriginSlug(homeSlugProp || null)
        if (!homeSlugProp) {
          setError('Asigna un Han de origen (current_territory_slug) para viajar.')
        } else {
          setError(null)
        }
      } else {
        setError(null)
      }
    } catch (e) {
      setError(e?.message || 'Error cargando viaje')
    } finally {
      setLoading(false)
    }
  }, [
    characterId,
    homeSlugProp,
    mode,
    observeAdventureId,
    onlyForThisAdventure,
    parentAdventureId,
  ])

  useEffect(() => {
    if (!observeAdventureId && !characterId) return undefined
    setLoading(true)
    refresh()
    const id = setInterval(refresh, POLL_MS)
    const onFocus = () => refresh()
    window.addEventListener('focus', onFocus)
    return () => {
      clearInterval(id)
      window.removeEventListener('focus', onFocus)
    }
  }, [characterId, observeAdventureId, refresh])

  useEffect(() => {
    if (homeSlugProp && homeSlugProp !== prevHomeRef.current) {
      prevHomeRef.current = homeSlugProp
      if (!session) setOriginSlug(homeSlugProp)
    }
  }, [homeSlugProp, session])

  useEffect(() => {
    if (session?.status !== 'traveling') return undefined
    setNowTick(Date.now())
    const id = setInterval(() => setNowTick(Date.now()), 1000)
    return () => clearInterval(id)
  }, [session?.id, session?.status, session?.eta, session?.pausedAt])

  const onStart = async () => {
    if (!characterId || !destSlug || !openingOk) return
    setBusy(true)
    try {
      const data = await startTravel(characterId, {
        destSlug,
        via,
        preferredRouteType: routeType,
        routeType,
        parentAdventureId: parentAdventureId || undefined,
        openingNarrative: openingTrimmed,
      })
      const finalDest =
        data?.itinerary?.finalDestSlug ||
        data?.itinerary?.waypoints?.[data.itinerary.waypoints.length - 1] ||
        destSlug
      toast.success(`Viaje iniciado hacia ${formatHanLabel(finalDest)}`)
      setOpeningNarrative('')
      setVia([])
      const advSlug = data?.adventure?.slug
      const startOpening = data?.opening
      if (advSlug && !parentAdventureId) {
        const hash =
          startOpening?.kind === 'setup' || !startOpening
            ? ADVENTURE_SETUP_ID
            : startOpening.kind === 'reply' && startOpening.replyId != null
              ? replyAnchorId(startOpening.replyId)
              : ADVENTURE_SETUP_ID
        navigate(`/aventura/${advSlug}#${hash}`)
        return
      }
      if (startOpening) setOpening(startOpening)
      await refresh()
    } catch (e) {
      toast.error(e?.message || 'No se pudo iniciar el viaje')
    } finally {
      setBusy(false)
    }
  }

  const isSessionOwner =
    !!session && characterId != null && Number(session.characterId) === Number(characterId)
  const canManualControl =
    mode !== 'monitor' &&
    !!session &&
    (isSessionOwner || !!isAdventureMaster || canTravelPausePerm)
  const canCancelTravel = canManualControl

  const pauseKind =
    session?.progress?.pauseKind === 'hard' ||
    session?.progress?.pauseKind === 'manual' ||
    session?.progress?.pauseKind === 'join_request' ||
    session?.progress?.pauseKind === 'leave'
      ? session.progress.pauseKind
      : pendingHard
        ? 'hard'
        : session?.status === 'event_paused'
          ? 'manual'
          : null

  const canPauseTravel = canManualControl && session?.status === 'traveling'
  const canResumeSoft =
    canManualControl &&
    session?.status === 'event_paused' &&
    (pauseKind === 'manual' || pauseKind === 'join_request' || pauseKind === 'leave') &&
    pendingJoinRequests.length === 0
  const canResumeHard =
    mode !== 'monitor' &&
    session?.status === 'event_paused' &&
    pauseKind === 'hard' &&
    (!!isAdventureMaster || canTravelGmPerm)

  const onCancel = async () => {
    if (!session?.id || !canCancelTravel) return
    setBusy(true)
    try {
      await cancelTravel(session.id, isSessionOwner ? characterId : null)
      toast.info('Viaje cancelado')
      await refresh()
    } catch (e) {
      toast.error(e?.message || 'No se pudo cancelar')
    } finally {
      setBusy(false)
    }
  }

  const onPause = async () => {
    if (!session?.id || !canPauseTravel) return
    setBusy(true)
    try {
      await pauseTravel(session.id, isSessionOwner ? characterId : null)
      toast.info('Viaje pausado')
      await refresh()
    } catch (e) {
      toast.error(e?.message || 'No se pudo pausar')
    } finally {
      setBusy(false)
    }
  }

  const onResume = async () => {
    if (!session?.id) return
    if (pauseKind === 'hard' && !canResumeHard) return
    if (
      (pauseKind === 'manual' || pauseKind === 'join_request' || pauseKind === 'leave') &&
      !canResumeSoft
    ) {
      return
    }
    setBusy(true)
    try {
      const data = await resumeTravel(session.id, {
        characterId: isSessionOwner ? characterId : null,
        eventId: pendingHard?.id,
      })
      const kicked = Array.isArray(data?.kickedCharacterIds) ? data.kickedCharacterIds.length : 0
      toast.success(
        kicked > 0
          ? `Viaje reanudado (${kicked} expulsado${kicked === 1 ? '' : 's'} de la crónica)`
          : 'Viaje reanudado'
      )
      await refresh()
    } catch (e) {
      toast.error(e?.message || 'No se pudo reanudar')
    } finally {
      setBusy(false)
    }
  }

  const onLeaveParty = async () => {
    if (!session?.id || !characterId) return
    const zoneLabel = formatHanLabel(session.currentZoneSlug || session.originSlug || '')
    if (
      !window.confirm(
        `¿Dejar el viaje y quedarte en ${zoneLabel || 'esta zona'}?\n\n` +
          'El viaje se pausa para despediros. Debes abandonar la crónica; si no, serás expulsado al reanudar. ' +
          'Si eres el líder y quedan compañeros, el liderazgo pasa a otro.'
      )
    ) {
      return
    }
    setBusy(true)
    try {
      const data = await leaveTravelParty(session.id, characterId)
      if (data?.action === 'leave_closed') {
        toast.info('Eras el último: viaje cerrado en la zona')
      } else {
        toast.info('Has dejado el viaje. Abandona la crónica cuando acabéis la despedida.')
      }
      await refresh()
    } catch (e) {
      toast.error(e?.message || 'No se pudo dejar el viaje')
    } finally {
      setBusy(false)
    }
  }

  const onClaim = async (spawnId) => {
    if (!session?.id || !characterId || !isSessionOwner) return
    setBusy(true)
    try {
      await claimTravelSpawn(session.id, spawnId, characterId)
      toast.success('Spawn reclamado')
      await refresh()
    } catch (e) {
      toast.error(e?.message || 'No se pudo reclamar el spawn')
    } finally {
      setBusy(false)
    }
  }

  const onJoinProposal = async () => {
    if (!proposal?.id || !characterId) return
    setBusy(true)
    try {
      await joinTravelProposal(proposal.id, characterId)
      toast.success('Te unes al viaje')
      await refresh()
    } catch (e) {
      toast.error(e?.message || 'No se pudo unir al viaje')
    } finally {
      setBusy(false)
    }
  }

  const onLeaveProposal = async () => {
    if (!proposal?.id || !characterId) return
    if (
      !window.confirm(
        '¿Quedarte en la zona? Saldrás de esta crónica. Rolea la despedida antes si hace falta.'
      )
    ) {
      return
    }
    setBusy(true)
    try {
      await leaveTravelProposal(proposal.id, characterId)
      toast.info('Has dejado la crónica')
      await refresh()
    } catch (e) {
      toast.error(e?.message || 'No se pudo salir')
    } finally {
      setBusy(false)
    }
  }

  const onStartProposal = async () => {
    if (!proposal?.id || !characterId) return
    if (
      !window.confirm(
        '¿Iniciar el viaje? Quienes no se hayan unido serán expulsados de la crónica.'
      )
    ) {
      return
    }
    setBusy(true)
    try {
      await startTravelProposal(proposal.id, characterId)
      toast.success('Viaje iniciado')
      await refresh()
    } catch (e) {
      toast.error(e?.message || 'No se pudo iniciar el viaje')
    } finally {
      setBusy(false)
    }
  }

  const onRequestJoin = async () => {
    if (!session?.id || !characterId) return
    setBusy(true)
    try {
      await requestJoinTravel(session.id, characterId)
      toast.info('Petición enviada. El viaje se ha pausado para rolear la unión.')
      await refresh()
    } catch (e) {
      toast.error(e?.message || 'No se pudo pedir unirse')
    } finally {
      setBusy(false)
    }
  }

  const onAcceptJoin = async (requestId) => {
    if (!requestId) return
    setBusy(true)
    try {
      await acceptJoinTravelRequest(requestId, isSessionOwner ? characterId : null)
      toast.success('Petición aceptada. Reanudad el viaje cuando terminéis el RP.')
      await refresh()
    } catch (e) {
      toast.error(e?.message || 'No se pudo aceptar')
    } finally {
      setBusy(false)
    }
  }

  const onRejectJoin = async (requestId) => {
    if (!requestId) return
    setBusy(true)
    try {
      await rejectJoinTravelRequest(requestId, isSessionOwner ? characterId : null)
      toast.info('Petición rechazada')
      await refresh()
    } catch (e) {
      toast.error(e?.message || 'No se pudo rechazar')
    } finally {
      setBusy(false)
    }
  }

  if (!observeAdventureId && !characterId) return null

  const canShowIdleStart = allowStart && mode !== 'monitor' && !!characterId
  if (!allowStart && (loading || (!session && !proposal))) {
    return null
  }

  const themeHref = adventureMeta?.slug ? `/aventura/${adventureMeta.slug}` : null
  const startHref = openingHref(opening, adventureMeta?.slug)

  const activeStatusMod = session
    ? statusModifier(session.status)
    : proposal
      ? 'muster'
      : 'idle'
  const hopProgress = session ? hopProgressRatio(session, nowTick) : 0
  const hopProgressPct = Math.round(hopProgress * 100)

  const myRoster = roster.find((r) => Number(r.characterId) === Number(characterId))
  const isProposer =
    !!proposal &&
    characterId != null &&
    Number(proposal.proposerCharacterId) === Number(characterId)
  const myChoice = myRoster?.choice || (isProposer ? 'join' : null)
  const canJoinProposal =
    mode !== 'monitor' &&
    !!proposal &&
    !!characterId &&
    !!myRoster &&
    myChoice !== 'join'
  const canLeaveProposal =
    mode !== 'monitor' &&
    !!proposal &&
    !!characterId &&
    !!myRoster &&
    !isProposer
  const canStartProposal =
    mode !== 'monitor' && !!proposal && isProposer && roster.some((r) => r.choice === 'join')

  const partyIds = Array.isArray(session?.progress?.partyCharacterIds)
    ? session.progress.partyCharacterIds.map(Number)
    : []
  const leavingIds = Array.isArray(session?.progress?.leavingCharacterIds)
    ? session.progress.leavingCharacterIds.map(Number).filter((id) => Number.isFinite(id) && id > 0)
    : []
  const zoneSlug = session?.currentZoneSlug || session?.originSlug || null
  const homeMatchesZone =
    !!homeSlugProp && !!zoneSlug && String(homeSlugProp).trim() === String(zoneSlug).trim()
  const alreadyInParty =
    !!characterId &&
    (Number(session?.characterId) === Number(characterId) ||
      partyIds.includes(Number(characterId)))
  const canLeaveParty =
    mode !== 'monitor' &&
    !!session &&
    !!characterId &&
    alreadyInParty &&
    ['traveling', 'event_paused'].includes(session.status)
  const myPendingJoin = pendingJoinRequests.find(
    (r) => Number(r.characterId) === Number(characterId)
  )
  const canRequestJoin =
    mode !== 'monitor' &&
    !!session &&
    !!characterId &&
    !!session.parentAdventureId &&
    homeMatchesZone &&
    !alreadyInParty &&
    !myPendingJoin &&
    ['traveling', 'event_paused'].includes(session.status)
  const canResolveJoin =
    mode !== 'monitor' &&
    !!session &&
    pendingJoinRequests.length > 0 &&
    (isSessionOwner || !!isAdventureMaster || canTravelPausePerm)

  return (
    <article
      className={`travel-panel char-card char-card--section travel-panel--${activeStatusMod} ${className}`.trim()}
      aria-label="Viaje"
    >
      <header className="char-section-header">
        <span className="char-section-header__ornament" aria-hidden="true" />
        <h3 className="char-section-header__title">Viaje</h3>
        <span className="char-section-header__ornament char-section-header__ornament--right" aria-hidden="true" />
      </header>

      {loading ? <p className="travel-panel__muted">Cargando estado de viaje…</p> : null}

      {error && !session && !proposal ? <p className="travel-panel__error">{error}</p> : null}

      {mode === 'monitor' && !loading && !session && !proposal ? (
        <p className="travel-panel__muted">Sin viaje activo. Inicia un tránsito desde tu Han actual.</p>
      ) : null}

      {!loading && proposal && !session ? (
        <div className="travel-panel__muster" role="status">
          <div className="travel-panel__hard-kicker">Viaje propuesto</div>
          <p className="travel-panel__muster-lead">
            Destino{' '}
            <strong>
              {formatHanLabel(
                proposal.waypoints?.[proposal.waypoints.length - 1] || proposal.originSlug
              )}
            </strong>
            . Únete al viaje o quédate en la zona (saldrás de la crónica). Cuando os hayáis
            despedido, el proponente inicia el reloj.
          </p>
          {itinerary?.waypoints?.length >= 2 ? (
            <p className="travel-panel__muster-path" aria-label="Itinerario propuesto">
              {itinerary.waypoints.map((slug, i) => (
                <React.Fragment key={`${slug}-${i}`}>
                  {i > 0 ? ' → ' : ''}
                  {formatHanLabel(slug)}
                </React.Fragment>
              ))}
            </p>
          ) : null}
          <ul className="travel-panel__muster-roster">
            {roster.map((r) => (
              <li key={r.characterId}>
                <span>
                  {r.characterName || `PJ #${r.characterId}`}
                  {r.isProposer ? ' (proponente)' : ''}
                </span>
                <span
                  className={`travel-panel__muster-choice travel-panel__muster-choice--${r.choice}`}
                >
                  {r.choice === 'join' ? 'Unido' : 'Pendiente'}
                </span>
              </li>
            ))}
          </ul>
          <div className="travel-panel__actions">
            {canJoinProposal ? (
              <button
                type="button"
                className="travel-panel__btn travel-panel__btn--primary"
                onClick={onJoinProposal}
                disabled={busy}
              >
                Unirme al viaje
              </button>
            ) : null}
            {canLeaveProposal ? (
              <button
                type="button"
                className="travel-panel__btn travel-panel__btn--ghost"
                onClick={onLeaveProposal}
                disabled={busy}
              >
                Quedarme (salir)
              </button>
            ) : null}
            {canStartProposal ? (
              <button
                type="button"
                className="travel-panel__btn travel-panel__btn--primary"
                onClick={onStartProposal}
                disabled={busy}
              >
                Iniciar viaje
              </button>
            ) : null}
            {startHref ? (
              <Link to={startHref} className="travel-panel__btn travel-panel__btn--ghost">
                Ver propuesta
              </Link>
            ) : null}
          </div>
          {isProposer ? (
            <p className="travel-panel__muted">
              Al iniciar, quienes sigan pendientes serán expulsados de la crónica.
            </p>
          ) : null}
        </div>
      ) : null}

      {mode === 'monitor' && session && themeHref ? (
        <p className="travel-panel__home">
          En camino:{' '}
          <Link to={themeHref} className="travel-panel__link">
            {adventureMeta?.title ||
              `${formatHanLabel(session.originSlug)} → ${formatHanLabel(session.destSlug)}`}
          </Link>
        </p>
      ) : null}

      {!loading && !session && canShowIdleStart ? (
        <div className="travel-panel__idle">
          <div className="travel-panel__route travel-panel__route--idle" aria-label="Origen">
            <div className="travel-panel__endpoint">
              <span className="travel-panel__endpoint-label">Origen</span>
              {originSlug ? (
                <Link to={`/han/${originSlug}`} className="travel-panel__endpoint-name">
                  {formatHanLabel(originSlug)}
                </Link>
              ) : (
                <span className="travel-panel__endpoint-name travel-panel__endpoint-name--muted">
                  sin Han asignado
                </span>
              )}
            </div>
            {provisionSummary ? (
              <p className="travel-panel__provisions" title="Según raciones en la mochila">
                Provisiones · {provisionSummary.label || provisionSummary.tier}
                {provisionSummary.units != null ? ` (${provisionSummary.units})` : ''}
              </p>
            ) : null}
          </div>

          {originSlug ? (
            <>
              <TravelItineraryPicker
                characterId={characterId}
                originSlug={originSlug}
                destSlug={destSlug}
                onDestSlugChange={setDestSlug}
                via={via}
                onViaChange={setVia}
                preferredRouteType={routeType}
                onPreferredRouteTypeChange={setRouteType}
                disabled={busy}
                idPrefix="travel-panel"
              />

              <div className="travel-panel__row">
                <label htmlFor="travel-opening">Apertura narrativa</label>
                <textarea
                  id="travel-opening"
                  className="travel-panel__textarea"
                  value={openingNarrative}
                  onChange={(e) => setOpeningNarrative(e.target.value)}
                  disabled={busy}
                  rows={4}
                  placeholder="Describe el inicio del tránsito (mín. 10 caracteres)…"
                  maxLength={8000}
                />
                <span
                  className={`travel-panel__hint${openingOk ? ' travel-panel__hint--ok' : ''}`}
                >
                  {openingTrimmed.length < OPENING_MIN
                    ? `${OPENING_MIN - openingTrimmed.length} caracteres más`
                    : 'Listo para partir'}
                </span>
              </div>

              <div className="travel-panel__actions">
                <button
                  type="button"
                  className="travel-panel__btn travel-panel__btn--primary"
                  onClick={onStart}
                  disabled={busy || !destSlug || !openingOk}
                >
                  {busy ? 'Iniciando…' : parentAdventureId ? 'Iniciar tramo' : 'Iniciar viaje'}
                </button>
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      {!loading && session ? (
        <div className="travel-panel__active">
          <div className="travel-panel__route" aria-label="Ruta del viaje">
            <div className="travel-panel__endpoint">
              <span className="travel-panel__endpoint-label">Desde</span>
              <Link
                to={`/han/${session.originSlug}`}
                className="travel-panel__endpoint-name"
              >
                {formatHanLabel(session.originSlug)}
              </Link>
            </div>
            <div className="travel-panel__path" aria-hidden="true">
              <span className="travel-panel__path-line" />
              <span className="travel-panel__path-tag">
                {ROUTE_TYPE_LABEL[session.routeType] || session.routeType}
              </span>
              <span className="travel-panel__path-line" />
            </div>
            <div className="travel-panel__endpoint travel-panel__endpoint--dest">
              <span className="travel-panel__endpoint-label">Hacia</span>
              <Link
                to={`/han/${session.destSlug}`}
                className="travel-panel__endpoint-name"
              >
                {formatHanLabel(session.destSlug)}
              </Link>
            </div>
          </div>

          {itinerary?.waypoints?.length >= 2 ? (
            <nav
              className="travel-panel__checkpoints"
              aria-label={`Itinerario, tramo ${(Number(itinerary.hopIndex) || 0) + 1} de ${Math.max(1, itinerary.waypoints.length - 1)}`}
            >
              <div className="travel-panel__checkpoints-head">
                <span className="travel-panel__checkpoints-kicker">Itinerario</span>
                <span className="travel-panel__checkpoints-meta">
                  Tramo {(Number(itinerary.hopIndex) || 0) + 1}/
                  {Math.max(1, itinerary.waypoints.length - 1)}
                </span>
              </div>
              <ol className="travel-panel__checkpoints-track">
                {itinerary.waypoints.map((slug, i) => {
                  const hop = Number(itinerary.hopIndex) || 0
                  const state =
                    i < hop ? 'done' : i === hop ? 'here' : i === hop + 1 ? 'next' : 'soon'
                  const isActiveBridge = i === hop + 1
                  const isDoneBridge = i <= hop
                  return (
                    <li
                      key={`${slug}-${i}`}
                      className={`travel-panel__checkpoint travel-panel__checkpoint--${state}`}
                    >
                      {i > 0 ? (
                        <span
                          className={`travel-panel__checkpoint-bridge${
                            isActiveBridge ? ' travel-panel__checkpoint-bridge--active' : ''
                          }${isDoneBridge ? ' travel-panel__checkpoint-bridge--done' : ''}`}
                          aria-hidden="true"
                        >
                          {isActiveBridge ? (
                            <span
                              className="travel-panel__checkpoint-bridge-fill"
                              style={{ width: `${hopProgressPct}%` }}
                            />
                          ) : null}
                        </span>
                      ) : null}
                      <span className="travel-panel__checkpoint-node" aria-hidden="true">
                        {state === 'done' ? '✓' : i + 1}
                      </span>
                      <Link
                        to={`/han/${slug}`}
                        className="travel-panel__checkpoint-label"
                        title={formatHanLabel(slug)}
                      >
                        {formatHanLabel(slug)}
                      </Link>
                    </li>
                  )
                })}
              </ol>
            </nav>
          ) : null}

          <div className="travel-panel__stats">
            <div className="travel-panel__stat">
              <span className="travel-panel__stat-label">Estado</span>
              <span
                className={`travel-panel__badge travel-panel__badge--${activeStatusMod}`}
              >
                {statusLabel(session.status)}
              </span>
            </div>
            <div className="travel-panel__stat travel-panel__stat--eta">
              <span className="travel-panel__stat-label">ETA</span>
              <span className="travel-panel__stat-value">{formatEta(session, nowTick)}</span>
            </div>
            <div className="travel-panel__stat">
              <span className="travel-panel__stat-label">Zona</span>
              <span className="travel-panel__stat-value">
                {session.currentZoneSlug
                  ? formatHanLabel(session.currentZoneSlug)
                  : '—'}
              </span>
            </div>
          </div>

          {session.status === 'event_paused' ? (
            <div className="travel-panel__hard" role="status">
              {pauseKind === 'join_request' ? (
                <>
                  <span className="travel-panel__hard-kicker">Pausa por unión</span>
                  <p>
                    El viaje está detenido para rolear la llegada de un compañero. El líder o el
                    Master aceptan o rechazan la petición; luego pueden reanudar.
                  </p>
                </>
              ) : pauseKind === 'leave' ? (
                <>
                  <span className="travel-panel__hard-kicker">Pausa por despedida</span>
                  <p>
                    Alguien deja el viaje. Rolead la despedida; quien se queda atrás debe
                    abandonar la crónica. Al reanudar, quien siga en la crónica sin estar en el
                    viaje será expulsado.
                  </p>
                </>
              ) : pauseKind === 'manual' ? (
                <>
                  <span className="travel-panel__hard-kicker">Pausa manual</span>
                  <p>
                    El reloj del viaje está detenido. El viajero o el Master pueden reanudarlo
                    cuando la escena lo permita.
                  </p>
                </>
              ) : (
                <>
                  <span className="travel-panel__hard-kicker">Evento en el camino</span>
                  {(() => {
                    const masterName = pendingHard?.assignedGm?.name || null
                    const masterLabel = masterName
                      ? `Master: ${masterName}`
                      : 'Master: sin asignar'
                    const replyId = pendingHard?.resolution?.replyId
                    const adventureSlug = adventureMeta?.slug
                    const replyHref =
                      replyId != null && adventureSlug
                        ? `/aventura/${adventureSlug}#${replyAnchorId(replyId)}`
                        : null

                    if (pendingHard?.status === 'awaiting_gm') {
                      return (
                        <p>
                          Esperando a {masterName || 'un Master'} para reanudar el camino.
                          {replyHref ? (
                            <>
                              {' '}
                              <Link to={replyHref} className="travel-panel__link">
                                Ver tu respuesta
                              </Link>
                            </>
                          ) : null}
                        </p>
                      )
                    }

                    return (
                      <p>
                        Obstáculo — responde en la crónica como siempre. {masterLabel}. Solo el
                        Master puede reanudar el viaje.
                      </p>
                    )
                  })()}
                </>
              )}
            </div>
          ) : null}

          {leavingIds.length > 0 && session.status === 'event_paused' ? (
            <div className="travel-panel__join-requests" role="status">
              <span className="travel-panel__hard-kicker">Se bajan del viaje</span>
              <ul className="travel-panel__muster-roster">
                {leavingIds.map((id) => (
                  <li key={id}>
                    <span>PJ #{id}</span>
                    <span className="travel-panel__muster-choice travel-panel__muster-choice--pending">
                      Debe abandonar la crónica
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {pendingJoinRequests.length > 0 ? (
            <div className="travel-panel__join-requests" role="status">
              <span className="travel-panel__hard-kicker">Peticiones de unión</span>
              <ul className="travel-panel__muster-roster">
                {pendingJoinRequests.map((r) => (
                  <li key={r.id}>
                    <span>
                      {r.characterName || `PJ #${r.characterId}`}
                      {r.zoneSlug ? ` · ${formatHanLabel(r.zoneSlug)}` : ''}
                    </span>
                    {canResolveJoin ? (
                      <span className="travel-panel__join-actions">
                        <button
                          type="button"
                          className="travel-panel__btn travel-panel__btn--small travel-panel__btn--primary"
                          onClick={() => onAcceptJoin(r.id)}
                          disabled={busy}
                        >
                          Aceptar
                        </button>
                        <button
                          type="button"
                          className="travel-panel__btn travel-panel__btn--small travel-panel__btn--ghost"
                          onClick={() => onRejectJoin(r.id)}
                          disabled={busy}
                        >
                          Rechazar
                        </button>
                      </span>
                    ) : (
                      <span className="travel-panel__muster-choice travel-panel__muster-choice--pending">
                        Pendiente
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="travel-panel__actions">
            {startHref ? (
              <Link to={startHref} className="travel-panel__btn travel-panel__btn--ghost">
                Ir al inicio del viaje
              </Link>
            ) : null}

            {mode === 'monitor' && themeHref ? (
              <Link to={themeHref} className="travel-panel__btn travel-panel__btn--primary">
                Abrir crónica de viaje
              </Link>
            ) : null}

            {canRequestJoin ? (
              <button
                type="button"
                className="travel-panel__btn travel-panel__btn--primary"
                onClick={onRequestJoin}
                disabled={busy}
              >
                Pedir unirme
              </button>
            ) : null}

            {myPendingJoin ? (
              <span className="travel-panel__muted">Petición de unión pendiente…</span>
            ) : null}

            {canPauseTravel ? (
              <button
                type="button"
                className="travel-panel__btn travel-panel__btn--ghost"
                onClick={onPause}
                disabled={busy}
              >
                Pausar viaje
              </button>
            ) : null}

            {canLeaveParty ? (
              <button
                type="button"
                className="travel-panel__btn travel-panel__btn--ghost"
                onClick={onLeaveParty}
                disabled={busy}
              >
                Dejar el viaje
              </button>
            ) : null}

            {canResumeSoft || canResumeHard ? (
              <button
                type="button"
                className="travel-panel__btn travel-panel__btn--primary"
                onClick={onResume}
                disabled={busy}
              >
                Reanudar viaje
              </button>
            ) : null}

            {canCancelTravel ? (
              <button
                type="button"
                className="travel-panel__btn travel-panel__btn--danger"
                onClick={onCancel}
                disabled={busy}
              >
                Cancelar viaje
              </button>
            ) : null}
          </div>

          {mode !== 'monitor' && isSessionOwner && spawns.length > 0 ? (
            <div className="travel-panel__spawns">
              <h4>Hallazgos en el camino</h4>
              <ul>
                {spawns.map((s) => (
                  <li key={s.id}>
                    <span>Spawn #{s.id}</span>
                    <button
                      type="button"
                      className="travel-panel__btn travel-panel__btn--small"
                      onClick={() => onClaim(s.id)}
                      disabled={busy}
                    >
                      Reclamar
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  )
}
